import { Download, RotateCcw } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";

import AnalyticsChartToolbarButton from "./AnalyticsChartToolbarButton";
import AnalyticsGraphChartCardShell from "./AnalyticsGraphChartCardShell";
import { type AnalyticsChartFrameHandle } from "./AnalyticsChartFrame";
import ChartViewFooter from "./ChartViewFooter";
import { buildGenericCustomChartOptions } from "./chartOptions/genericCustomChart";
import { useSingleAnalyticsGraphChartData } from "./hooks/useAnalyticsGraphChartData";
import { useDateRangeZoomHistory } from "./hooks/useDateRangeZoomHistory";
import { formatDateRangeKey } from "./utils/cloneDateRange";
import { sanitizeChartDownloadFileName } from "./utils/sanitizeChartDownloadFileName";

import type {
  AgChartsGraphArtifact,
  AnalyticsGraphDataSource,
  AnalyticsGraphToolChartData,
  DashboardChartSpecRequest,
} from "@/queries/analytics-custom-charts-types";
import {
  AnalyticsGraphDataSource as AnalyticsGraphDataSourceValue,
  getDefaultViewId,
  resolveDashboardChartView,
  viewToCustomChartSpec,
} from "@/queries/analytics-custom-charts-types";
import { cn } from "@/lib/utils";

interface AnalyticsQueryChartCardProps {
  chart: DashboardChartSpecRequest;
  dataSource?: AnalyticsGraphDataSource;
  artifactTitle: string;
  artifactFilterGroup?: AgChartsGraphArtifact["filterGroup"];
  className?: string;
  onChartDataChange?: (chartData: AnalyticsGraphToolChartData) => void;
}

const selectedChartViewCache = new Map<string, string>();

const getInitialSelectedViewId = (chart: DashboardChartSpecRequest, cacheKey: string) => {
  const cachedViewId = selectedChartViewCache.get(cacheKey);
  if (cachedViewId && chart.views?.some(view => view.id === cachedViewId)) {
    return cachedViewId;
  }
  return getDefaultViewId(chart) ?? chart.views?.[0]?.id ?? "";
};

const AnalyticsQueryChartCard = observer(
  ({
    chart,
    dataSource,
    artifactTitle,
    artifactFilterGroup,
    className,
    onChartDataChange,
  }: AnalyticsQueryChartCardProps) => {
    const chartFrameRef = useRef<AnalyticsChartFrameHandle>(null);
    const views = useMemo(() => chart.views ?? [], [chart.views]);
    const cacheKey = `${artifactTitle}:${chart.id}`;
    const [selectedViewId, setSelectedViewId] = useState(() =>
      getInitialSelectedViewId(chart, cacheKey),
    );
    const setPersistedSelectedViewId = (viewId: string) => {
      selectedChartViewCache.set(cacheKey, viewId);
      setSelectedViewId(viewId);
    };
    const selectedView = useMemo(
      () => resolveDashboardChartView(chart, selectedViewId),
      [chart, selectedViewId],
    );
    const customChartSpec = useMemo(
      () => (selectedView ? viewToCustomChartSpec(chart, selectedView) : undefined),
      [chart, selectedView],
    );
    const {
      canResetZoom,
      localDateRangeOverride,
      resetDateRangeFromChart,
      selectDateRangeFromChart,
    } = useDateRangeZoomHistory();
    const { chartInterval, chartResponse, isAnalyticsLoading, structuredDateRange } =
      useSingleAnalyticsGraphChartData(customChartSpec, {
        dataSource,
        dateRangeOverride: localDateRangeOverride,
        filterGroup: artifactFilterGroup,
      });

    const isTimeSeriesChart = Boolean(selectedView?.timeSeries);
    const viewItems = views.map(view => ({ label: view.label, value: view.id }));
    const hasMultipleViews = views.length > 1;
    const dataLength = chartResponse?.data.length ?? 0;
    const selectedViewLabel = views.find(view => view.id === selectedViewId)?.label;
    const chartDataForToolResponse = useMemo<AnalyticsGraphToolChartData | undefined>(
      () =>
        chartResponse
          ? {
              chartId: chartResponse.id,
              chartTitle: chartResponse.title,
              selectedViewId,
              selectedViewLabel,
              chartInterval,
              data: chartResponse.data,
              series: chartResponse.series,
              derivedInsights: chartResponse.derivedInsights,
            }
          : undefined,
      [chartInterval, chartResponse, selectedViewId, selectedViewLabel],
    );
    const downloadFileName = sanitizeChartDownloadFileName(
      artifactTitle,
      chart.title,
      selectedViewLabel,
    );
    const isLoading = isAnalyticsLoading;
    const canDownload = Boolean(chartResponse) && !isAnalyticsLoading && dataLength > 0;
    const structuredDateRangeKey = structuredDateRange
      ? formatDateRangeKey(structuredDateRange)
      : "unbounded";
    const chartResetKey = `${chart.id}:${selectedViewId}:${structuredDateRangeKey}:${dataLength}`;
    const chartOptions = chartResponse
      ? buildGenericCustomChartOptions(chartResponse, {
          chartInterval,
          countAxisTitle:
            dataSource === AnalyticsGraphDataSourceValue.Traces ? "Traces" : undefined,
          structuredDateRange,
          onDateRangeSelect: structuredDateRange
            ? range => selectDateRangeFromChart(range, structuredDateRange)
            : undefined,
        })
      : {};

    useEffect(() => {
      if (chartDataForToolResponse) {
        onChartDataChange?.(chartDataForToolResponse);
      }
    }, [chartDataForToolResponse, onChartDataChange]);

    return (
      <div className={cn("min-w-0", className)}>
        <AnalyticsGraphChartCardShell
          title={chart.title}
          chartOptions={chartOptions}
          resetKey={chartResetKey}
          isLoading={isLoading}
          isEmpty={!isLoading && dataLength === 0}
          className="h-full w-full"
          chartFrameRef={chartFrameRef}
          footer={
            <ChartViewFooter
              hasMultipleViews={hasMultipleViews}
              selectedViewId={selectedViewId}
              setSelectedViewId={setPersistedSelectedViewId}
              viewItems={viewItems}
            />
          }
          toolbarActions={
            <>
              {isTimeSeriesChart && (
                <AnalyticsChartToolbarButton
                  ariaLabel="Reset chart zoom"
                  disabled={!canResetZoom || isLoading}
                  icon={RotateCcw}
                  onClick={resetDateRangeFromChart}
                  tooltip="Reset zoom"
                />
              )}
              <AnalyticsChartToolbarButton
                ariaLabel="Download chart"
                disabled={!canDownload}
                icon={Download}
                onClick={() => chartFrameRef.current?.downloadChart(downloadFileName)}
                tooltip="Download chart"
              />
            </>
          }
        />
      </div>
    );
  },
);

export default AnalyticsQueryChartCard;
